/**
 * Simple EventEmitter stub compatible with React Native's vendor EventEmitter
 * Provides addListener/removeListener/removeAllListeners/emit API
 */

type Listener = { handler: (...args: any[]) => void };

export default class EventEmitter {
  private _events: Map<string, Set<Listener>>;

  constructor() {
    this._events = new Map();
  }

  addListener(eventName: string, handler: (...args: any[]) => void) {
    if (!this._events.has(eventName)) this._events.set(eventName, new Set());
    const set = this._events.get(eventName)!;
    const listener = { handler };
    set.add(listener);
    return {
      remove: () => set.delete(listener),
    };
  }

  removeListener(eventName: string, handler: (...args: any[]) => void) {
    const set = this._events.get(eventName);
    if (!set) return;
    for (const l of Array.from(set)) {
      if (l.handler === handler) set.delete(l);
    }
  }

  removeAllListeners(eventName?: string) {
    if (typeof eventName === 'string') {
      this._events.delete(eventName);
    } else {
      this._events.clear();
    }
  }

  emit(eventName: string, ...args: any[]) {
    const set = this._events.get(eventName);
    if (!set) return;
    for (const l of Array.from(set)) {
      try {
        l.handler(...args);
      } catch (e) {
        // swallow errors in handlers to match native behavior
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
  }
}
